import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * Segmented EN / हिंदी toggle for the preferred coach-response language.
 *
 * Default (no props): reads and writes the STUDENT AuthContext directly, so it
 * can be dropped anywhere inside the authenticated student tree — unchanged
 * behavior since the Hindi toggle first shipped.
 *
 * Prop-driven (value + onChange): controlled mode for other audiences — the
 * teacher/facilitator "Ask the coach" header passes its own language state
 * from TeacherAuthContext. The choice persists server-side either way and
 * takes effect on the next coach reply.
 */
const OPTIONS: { value: 'en' | 'hi'; label: string }[] = [
  { value: 'en', label: 'EN' },
  { value: 'hi', label: 'हिंदी' },
];

interface LanguageToggleProps {
  className?: string;
  /** Controlled mode: current language. When omitted, student AuthContext is used. */
  value?: 'en' | 'hi';
  /** Controlled mode: persist the new language. When omitted, student AuthContext is used. */
  onChange?: (value: 'en' | 'hi') => Promise<void> | void;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({ className = '', value, onChange }) => {
  const auth = useAuth();
  const controlled = value !== undefined && onChange !== undefined;
  const language = controlled ? value : auth.language;
  const [saving, setSaving] = useState<'en' | 'hi' | null>(null);

  const handleSelect = async (next: 'en' | 'hi') => {
    if (next === language || saving) return;
    setSaving(next);
    try {
      if (controlled) {
        await onChange(next);
      } else {
        await auth.setLanguage(next);
      }
    } catch (err) {
      console.error('Could not change language:', err);
    } finally {
      setSaving(null);
    }
  };

  return (
    <div
      className={`inline-flex items-center rounded-lg border border-slate-200 bg-slate-50 p-0.5 ${className}`}
      role="group"
      aria-label="Coach language"
    >
      {OPTIONS.map((opt) => {
        const active = language === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => handleSelect(opt.value)}
            disabled={saving !== null}
            aria-pressed={active}
            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-colors disabled:opacity-60 ${
              active
                ? 'bg-violet-600 text-white shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {saving === opt.value ? '…' : opt.label}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageToggle;
